import React, { useState, useEffect } from 'react'
import { PageLayout } from '../../components/layout/PageLayout'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { ToastContainer } from '../../components/ui/Toast'
import { CertificationDialog } from '../../components/certifications/CertificationDialog'
import { useToast } from '../../hooks/useToast'
import { useDebounce } from '../../hooks/useDebounce'
import { CertificationsService } from '../../services/api/certificationsService'
import { Certification } from '../../types/certification'
import { logger } from '../../utils/logger'
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  PhotoIcon,
  FunnelIcon
} from '@heroicons/react/24/outline'

export const CertificationsPage: React.FC = () => {
  const [certifications, setCertifications] = useState<Certification[]>([])
  const [filteredCertifications, setFilteredCertifications] = useState<Certification[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, 500)
  const { toasts, showToast, removeToast } = useToast()

  // Dialog states
  const [showCertificationDialog, setShowCertificationDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [selectedCertification, setSelectedCertification] = useState<Certification | undefined>(undefined)
  const [certificationToDelete, setCertificationToDelete] = useState<Certification | null>(null)

  useEffect(() => {
    fetchCertifications()
  }, [])

  useEffect(() => {
    filterCertifications()
  }, [debouncedSearchQuery, certifications])

  const fetchCertifications = async () => {
    setLoading(true)
    try {
      const data = await CertificationsService.getCertifications()
      // Sort by priority
      const sortedData = [...data].sort((a, b) => a.priority - b.priority)
      setCertifications(sortedData)
      setFilteredCertifications(sortedData)
    } catch (error) {
      logger.error('Error fetching certifications:', error)
      showToast('Erreur lors du chargement des certifications', 'error')
    } finally {
      setLoading(false)
    }
  }

  const filterCertifications = () => {
    if (!debouncedSearchQuery.trim()) {
      setFilteredCertifications(certifications)
      return
    }

    const filtered = certifications.filter(cert =>
      cert.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
    )
    setFilteredCertifications(filtered)
  }

  const handleAdd = () => {
    setSelectedCertification(undefined)
    setShowCertificationDialog(true)
  }

  const handleEdit = (certification: Certification) => {
    setSelectedCertification(certification)
    setShowCertificationDialog(true)
  }

  const handleDelete = (certification: Certification) => {
    setCertificationToDelete(certification)
    setShowDeleteDialog(true)
  }

  const submitCertification = async (certification: Certification) => {
    try {
      if (certification.id) {
        // Update existing
        const updated = await CertificationsService.updateCertification(certification)
        setCertifications(prevCerts => {
          const newCerts = prevCerts.map(c => c.id === updated.id ? updated : c)
          return newCerts.sort((a, b) => a.priority - b.priority)
        })
        showToast('Certification modifiée avec succès', 'success')
      } else {
        // Create new
        const created = await CertificationsService.createCertification(certification)
        setCertifications(prevCerts => {
          const newCerts = [...prevCerts, created]
          return newCerts.sort((a, b) => a.priority - b.priority)
        })
        showToast('Certification créée avec succès', 'success')
      }
      setShowCertificationDialog(false)
    } catch (error) {
      logger.error('Error saving certification:', error)
      showToast('Erreur lors de l\'enregistrement de la certification', 'error')
      throw error
    }
  }

  const confirmDelete = async () => {
    if (!certificationToDelete?.id) return

    try {
      await CertificationsService.deleteCertification(certificationToDelete.id)
      setCertifications(prevCerts => prevCerts.filter(c => c.id !== certificationToDelete.id))
      showToast('Certification supprimée avec succès', 'success')
      setShowDeleteDialog(false)
      setCertificationToDelete(null)
    } catch (error) {
      logger.error('Error deleting certification:', error)
      showToast(
        'Erreur lors de la suppression de la certification. Il est possible que des produits soient associés à cette certification.',
        'error'
      )
    }
  }

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout maxWidth="5xl">
      <ToastContainer toasts={toasts} onClose={removeToast} />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
          Certifications et labels
        </h1>
        <Button
          onClick={fetchCertifications}
          variant="outline"
          className="flex items-center gap-2"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Rafraîchir
        </Button>
      </div>

      {/* Filters Card */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <FunnelIcon className="h-5 w-5" />
              Filtres
            </CardTitle>
            <Button
              onClick={handleAdd}
              variant="primary"
              className="flex items-center gap-2"
            >
              <PlusIcon className="h-4 w-4" />
              Nouvelle certification
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher une certification..."
              className="w-full pl-10"
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Certifications Table */}
      <Card>
        <CardContent className="p-0">
          {filteredCertifications.length === 0 ? (
            <div className="p-8 text-center text-gray-600 dark:text-gray-400">
              {searchQuery ? 'Aucune certification trouvée' : 'Aucune certification'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800 border-b dark:border-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-normal text-gray-900 dark:text-white uppercase tracking-wider">
                      Image
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-normal text-gray-900 dark:text-white uppercase tracking-wider">
                      Nom
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-normal text-gray-900 dark:text-white uppercase tracking-wider">
                      Priorité
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-normal text-gray-900 dark:text-white uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredCertifications.map((certification) => (
                    <tr
                      key={certification.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                      onClick={() => handleEdit(certification)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                          {certification.image ? (
                            <img
                              src={`data:image/png;base64,${certification.image}`}
                              alt={certification.name}
                              className="w-full h-full object-cover"
                            />
                          ) : certification.imageURL ? (
                            <img
                              src={certification.imageURL}
                              alt={certification.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none'
                                const parent = e.currentTarget.parentElement
                                if (parent) {
                                  const icon = document.createElement('div')
                                  icon.className = 'flex items-center justify-center w-full h-full'
                                  icon.innerHTML = '<svg class="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>'
                                  parent.appendChild(icon)
                                }
                              }}
                            />
                          ) : (
                            <PhotoIcon className="h-6 w-6 text-gray-400" />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {certification.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {certification.priority}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleEdit(certification)
                            }}
                            className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDelete(certification)
                            }}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Certification Dialog */}
      <CertificationDialog
        isOpen={showCertificationDialog}
        onClose={() => {
          setShowCertificationDialog(false)
          setSelectedCertification(undefined)
        }}
        onSubmit={submitCertification}
        certification={selectedCertification}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false)
          setCertificationToDelete(null)
        }}
        onConfirm={confirmDelete}
        title="Supprimer la certification"
        message={`Êtes-vous sûr de vouloir supprimer la certification "${certificationToDelete?.name}" ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
      />
    </PageLayout>
  )
}